import type { CpmCelulaResponse } from '@triade/contracts';
import { decimalDigitado, formatarCustoNumero, formatarDecimal } from '@/lib/format';

const ID = /^[A-Za-z0-9_-]{1,64}$/;
const COMPETENCIA = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * Busca da URL `/custos/centros`: `visao=cpm` abre a grade; `centro` = id (ou `novo`) abre o formulario; `pai` = id do sintetico
 * de um centro novo ("+ filho", sem `pai` = raiz); `ate` = ultimo mes da grade.
 */
export interface BuscaCentros {
  visao?: 'cpm';
  centro?: string;
  pai?: string;
  ate?: string;
}

export function lerBuscaCentros(bruto: Record<string, unknown>): BuscaCentros {
  const centro = typeof bruto.centro === 'string' && (bruto.centro === 'novo' || ID.test(bruto.centro)) ? bruto.centro : undefined;
  const pai = centro === 'novo' && typeof bruto.pai === 'string' && ID.test(bruto.pai) ? bruto.pai : undefined;
  const ate = typeof bruto.ate === 'string' && COMPETENCIA.test(bruto.ate) ? bruto.ate : undefined;
  return {
    ...(bruto.visao === 'cpm' ? { visao: 'cpm' as const } : {}),
    ...(centro ? { centro } : {}),
    ...(pai ? { pai } : {}),
    ...(ate ? { ate } : {}),
  };
}

/** Tira os zeros a direita de um decimal da API ("0.2753500000" -> "0.27535"; "9600.000000" -> "9600"). So reescreve texto. */
export function semZeros(valor: string): string {
  if (!valor.includes('.')) return valor;
  return valor.replace(/0+$/, '').replace(/\.$/, '');
}

/** CPM para exibir: pt-BR, nas casas de custo/CPM da empresa (HALF_UP). */
export function cpmParaTexto(valor: string | null | undefined): string {
  return valor == null ? '—' : formatarCustoNumero(valor);
}

/** Minutos para exibir: pt-BR, sem zeros de escala ("9.600", "7.200,5"). */
export function minutosParaTexto(valor: string | null | undefined): string {
  return valor == null ? '—' : formatarDecimal(semZeros(valor), 0);
}

/** Texto digitado ("0,27535", "1.5") -> string decimal do CPM (ate 10 casas, sem sinal); invalido/vazio = null. */
export function cpmDigitado(texto: string): string | null {
  const d = decimalDigitado(texto);
  return d !== null && /^\d{1,8}(\.\d{1,10})?$/.test(d) ? d : null;
}

/** Texto digitado de minutos -> string decimal (ate 6 casas, sem sinal); invalido/vazio = null. */
export function minutosDigitado(texto: string): string | null {
  const d = decimalDigitado(texto);
  return d !== null && /^\d{1,12}(\.\d{1,6})?$/.test(d) ? d : null;
}

/** Mesmo numero? (comparacao textual sem zeros de escala; nao ha conta). */
export function mesmoDecimal(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a == null || b == null) return a == b;
  const na = semZeros(a.includes('.') ? a : `${a}`);
  const nb = semZeros(b.includes('.') ? b : `${b}`);
  return na.replace(/^0+(?=\d)/, '') === nb.replace(/^0+(?=\d)/, '');
}

/** "2026-10" -> "out/2026". */
const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
export function rotuloCompetencia(c: string): string {
  const [a, m] = c.split('-');
  return `${MESES[Number(m) - 1] ?? '?'}/${a}`;
}

/** Soma `n` meses a "AAAA-MM" (so calendario). */
export function somarMeses(c: string, n: number): string {
  const [a, m] = c.split('-').map(Number) as [number, number];
  const idx = a * 12 + (m - 1) + n;
  return `${String(Math.floor(idx / 12)).padStart(4, '0')}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

/** Mes corrente "AAAA-MM" (data local). */
export function competenciaAtual(agora: Date = new Date()): string {
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
}

export function legendaCelula(c: CpmCelulaResponse): string | null {
  const partes = [c.origem === 'IMPORTADO_XARP' ? 'xarp' : c.origem === 'RATEIO' ? 'rateio' : '', c.versao > 1 ? `v${c.versao}` : ''].filter(Boolean);
  return partes.length > 0 ? partes.join(' · ') : null;
}
